<template>
  <div class="position-relative h-100">

    <b-form
      novalidate
      ref="form"
      :validated="formValidated">
      <b-row class="no-gutters">

        <b-col :md="formColumns">

          <b-row
            v-for="(input, index) in fields"
            :key="`${index}${input.name}`"
            v-if="!!fields && showInput(input.name) && comparator(input)"
            :class="[`mb-${mb}`]"
          >
            <b-col :md="labelBlockWith" v-if="input.type !== 'switch' && input.label">
              <label :for="input.name" class="pr-0">
                {{ editing && input.label_editing ? input.label_editing : input.label}}
                <span class="text-danger" v-if="input.required">{{$t('form.required_star')}}</span>
              </label>
              <custom-tooltip class="ml-2" v-if="input.tooltipName && input.tooltipText" :name="getTooltipName(input.tooltipName)">
                {{ $t(input.tooltipText) }}
              </custom-tooltip>
            </b-col>
            <b-col
              :class="[input.name === 'price' || input.type === 'price' || isDiscountConditionalVisible(input, 'amount') ? 'd-flex flex-row' : '']"
              class="position-relative"
            >
              <multiselect
                v-if="input.type === 'select' && selectOptions[input.name]"
                :loading="isSelectLoading[input.name]"
                v-model="params[input.name]"
                :multiple="input.multiple"
                :id="input.name"
                :label="input.labelKey || 'name'"
                :track-by="input.trackBy || 'name'"
                :group-values="input.groupValues || null"
                :group-label="input.groupLabel || null"
                :group-select="false"
                :preselect-first="input.preselectFirst"
                :placeholder="input.placeholder"
                :options="selectOptions[input.name]"
                :searchable="input.searchable"
                :taggable="input.taggable"
                :allow-empty="input.allowEmpty"
                :close-on-select="!input.multiple"
                @open="id => {changeHighlightedOption(id)}"
                @search-change="query => {debounceSearchSelectOption(input.options_url, input.name, input.filter_field, query)}"
                @tag="value => {addTag(input.name, value)}"
                @remove="value => {debounceCreateParamsObject(input.name, value)}"
                @input="value => {debounceCreateParamsObject(input.name, value)}"
                select-label=""
                selected-label=""
                :deselect-label="input.clearable == undefined || input.clearable ? 'X ' : ''"
                tag-placeholder="+"
                :disabled="input.hasOwnProperty('disabled') && input.disabled === true"
              >
                <template slot="noResult">
                  {{ $t('global.no_results') }}
                </template>
              </multiselect>

              <b-input-group class="position-relative">
                <FormFieldInput
                  v-if="input.type === 'text'
                    ||( input.type === 'number' && input.name !== 'price' && input.name !== 'trial_price')
                    || input.type === 'tel'
                    || input.type === 'email'
                    || input.type === 'password'
                    || input.type === 'url'
                    || isDiscountConditionalVisible(input, 'percent')"
                  v-model="params[input.name]"
                  :read-only="input.readOnly"
                  :max-length="input.maxLength"
                  :password-visible="inputPasswordVisible"
                  :type="input.type"
                  :placeholder="input.placeholder"
                  :name="input.name"
                  :id="input.name"
                  :min="input.min"
                  :max="input.max"
                  :step="input.step"
                  :required="input.required"
                  :autocomplete="input.autocomplete"
                  @field-input="({name, inputValue}) => {debounceCreateParamsObject(name, inputValue)}"
                />

                <money
                    class="rounded w-100"
                    style="box-shadow: none"
                    v-if="input.name === 'trial_price' || input.name === 'price' || input.type === 'price' || isDiscountConditionalVisible(input, 'amount')"
                    v-bind="money"
                    :value="params[input.name]"
                    @input="(value) => priceInput(value, input.name)"
                />
                <div class="invalid-feedback position-absolute mt-0 text-left" style="bottom: -16px">
                  {{$t('errors.error_validation')}}
                </div>
                <i v-if="input.type === 'password'"
                   class="fas position-absolute text-black input-icon"
                   :class="!!inputPasswordVisible ? 'fa-eye' : ' fa-eye-slash'"
                   @click="inputPasswordVisible = !inputPasswordVisible"></i>

                <DatePickerInput
                  v-if="input.type === 'date'"
                  :is-range="input.isRange"
                  v-model="params[input.name]"
                  :with-time="input.hasOwnProperty('withTime') ? input.withTime : true"
                  :mode="input.mode"
                  @input="value => {createParamsObject(input.name, value)}"
                  :min-date="params[input.min]"
                  :max-date="params[input.max]"
                  :model-config="input.modelConfig"
                  :disabled="input.hasOwnProperty('disabled') ? input.disabled : false"
                />
              </b-input-group>

              <b-input-group v-if="input.type === 'range'">
                <b-row>
                  <b-col v-for="item in input.options" :key="item.name">
                    <b-form-input
                      class="rounded w-100"
                      min="1"
                      v-model="params[item.name]"
                      type="number"
                    />
                  </b-col>
                </b-row>
              </b-input-group>

              <multiselect
                v-if="(input.name === 'price' ||
                  input.type === 'price' ||
                  isDiscountConditionalVisible(input, 'amount')) && input.currency !== false"
                v-model="params.currency"
                class="w-50 ml-4"
                id="currency"
                label="label"
                track-by="label"
                :preselect-first="true"
                placeholder=""
                :options="currencyOptions"
                :searchable="false"
                :allow-empty="false"
                :close-on-select="true"
                @remove="value => {debounceCreateParamsObject('currency', value)}"
                @input="value => {debounceCreateParamsObject('currency', value)}"
                select-label=""
                selected-label=""
                deselect-label=""
              >
                <template slot="noResult">
                  {{ $t('global.no_results') }}
                </template>
              </multiselect>

              <b-form-textarea
                class="rounded w-100"
                v-if="input.type === 'textarea'"
                :required="input.required"
                rows="6"
                v-model="params[input.name]"
              />
              <div class="invalid-feedback position-absolute mt-0 text-left" style="bottom: -16px">
                {{ $t('errors.error_validation') }}
              </div>

              <div
                class="d-flex align-items-end flex-column"
                v-if="input.type === 'switch' && !inline"
              >
                <h6 class="text-uppercase font-weight-normal text-right">{{ input.label }}</h6>
                <div class="d-flex flex-column align-items-end">
                  <label class="switch switch-3d switch-success">
                    <b-form-checkbox type="checkbox"
                                     class="switch-input"
                                     v-model="params[input.name]"
                                     @input="value => {createParamsObject(input.name, value, input.field)}"
                    />
                    <input type="checkbox"
                           class="switch-input"
                           :checked="params[input.name]"
                    >
                    <span class="switch-slider"></span>
                  </label>
                  <span v-if="input.title && params[input.name]"
                        class="d-inline text-success pr-2">{{input.title}}</span>
                  <span v-if="input.title && !params[input.name]"
                        class="d-inline text-muted pr-2">{{input.titleFalse}}</span>
                </div>
              </div>

              <b-row v-if="input.type === 'switch' && inline" :class="{'no-gutters' : 'no_gutters'}">
                <b-col :md="labelBlockWith">
                  <label :for="input.name" class="pr-0 mb-0">{{input.label}}</label>
                  <custom-tooltip class="ml-2" v-if="input.tooltipName && input.tooltipText" :name="getTooltipName(input.tooltipName)">
                    {{ $t(input.tooltipText) }}
                  </custom-tooltip>
                </b-col>
                <b-col class="d-flex align-items-center">
                  <label class="switch switch-3d switch-success mb-0">
                    <b-form-checkbox type="checkbox"
                                     class="switch-input"
                                     v-model="params[input.name]"
                                     @input="value => {createParamsObject(input.name, value, input.field)}"
                                     :disabled="input.hasOwnProperty('disabled') ? input.disabled : false"
                    />
                    <input type="checkbox"
                           class="switch-input"
                           :checked="params[input.name]"
                    >
                    <span class="switch-slider"></span>
                  </label>
                  <span v-if="input.title && params[input.name]"
                        class="d-inline text-success pl-2">{{input.title}}</span>
                  <span v-if="input.title && !params[input.name]"
                        class="d-inline text-muted pl-2">{{input.titleFalse}}</span>
                </b-col>
              </b-row>

              <FormFieldRepeater v-if="input.type === 'repeater'"
                                 :params-key="input.name"
                                 :fields="input.fields"
                                 :prefill-params="prefillParams"/>

            </b-col>
          </b-row>
        </b-col>

        <b-col
          class="d-flex justify-content-end align-self-start">
          <b-btn
            v-if="showClearAccountButton && editing"
            variant="default"
            v-b-tooltip.hover
            :title="$t('users.clean_up_account_hit')"
            class="text-secondary"
            @click="clearAccount"
          >
            {{ $t('button.clear_account') }}
          </b-btn>
          <b-btn
            v-if="showSubmitButton"
            @click="submit"
            class="align-self-end btn-width w-100 input-height ml-2"
          >
            {{ $t('button.save') }}
          </b-btn>
        </b-col>

      </b-row>

    </b-form>

  </div>
</template>

<script>
  import {Money} from 'v-money'
  import FormFieldRepeater from '../forms/fields/formFieldRepeater';
  import FormFieldInput from '../forms/fields/formFieldInput';
  import {formatDate} from "vue2-datepicker/src/utils";
  import DatePickerInput from "../forms/fields/datePickerInput";

  export default {
    name: 'formFields',
    middleware: 'auth',
    props: {
      fields: {
        type: Array,
        require: true
      },
      labelBlockWith: {
        type: Number,
        default: 3
      },
      prefillParams: {
        type: Object
      },
      editing: {
        type: Boolean,
        default: false
      },
      mb: {
        type: Number,
        default: 4
      },
      inline: {
        type: Boolean,
        default: false
      },
      showSubmitButton: {
        type: Boolean,
        default: true
      },
      showClearAccountButton: {
        type: Boolean,
        default: false
      },
      formColumns: {
        type: Number,
        default: 12
      },
      emitSubmitForm: {
        type: Boolean,
        default: false
      },
      clearFields: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        formValidated: false,
        isSelectLoading: {},
        loadingOptions: {},
        params: {},
        userTags: [],
        selectOptions: {},
        inputPasswordVisible: false,
        mailchimp_categories: [],
        money: {
          decimal: '.',
          thousands: ',',
          prefix: '',
          suffix: '',
          precision: 2,
          masked: false
        },
        debounceCreateParamsObject: () => {
        },
        debounceSearchSelectOption: () => {
        },
      }
    },
    components: {
      DatePickerInput,
      Money,
      FormFieldRepeater,
      FormFieldInput
    },
    async mounted() {
      this.$store.dispatch('currency/getCurrency');
      if (!!this.prefillParams) {
        this.params = this.prefillParams;
        if (this.prefillParams.price) {
          this.params.price = this.formatToDollars(this.prefillParams.price);
        }
        if (this.prefillParams.trial_price) {
          this.params.trial_price = this.formatToDollars(this.prefillParams.trial_price);
        }
        if (this.prefillParams.currency && typeof this.params.currency === 'string') {
          this.params.currency = this.formatOption(this.prefillParams.currency, 'label');
        }
        if (this.prefillParams.period) {
          this.params.period = this.formatOption(this.prefillParams.period);
        }
        if (this.prefillParams.type) {
          this.params.type = this.formatOption(this.prefillParams.type);
        }
        if (this.prefillParams.trial_period) {
          this.params.trial_period = this.formatOption(this.prefillParams.trial_period);
        }
        this.$forceUpdate()
      } else {
        this.params.active = true
      }

      this.getSelectOptions();
      this.getSelectValues();
      this.debounceCreateParamsObject = _.debounce(this.createParamsObject, 500);
      this.debounceSearchSelectOption = _.debounce(this.searchSelectOption, 500);
    },
    computed: {
      selects() {
        return _.filter(this.fields, input => input.type === 'select' && !input.group);
      },
      currencyOptions() {
        return this.$store.getters['currency/currency']
      }
    },
    methods: {
      comparator(field) {
        if (!field.conditional) return true;

        let { field: fieldKey, value, operator } = field.conditional;

        if (this.params[fieldKey] !== undefined) {
          switch (operator) {
            case '>':   return this.getValueOf(this.params[fieldKey]) > value;
            case '<':   return this.getValueOf(this.params[fieldKey]) < value;
            case '>=':  return this.getValueOf(this.params[fieldKey]) >= value;
            case '<=':  return this.getValueOf(this.params[fieldKey]) <= value;
            case '===': return this.getValueOf(this.params[fieldKey]) === value;
            case '!==': return this.getValueOf(this.params[fieldKey]) !== value;
          }
        } else {
          return true;
        }
      },
      getValueOf(value) {
        if (value === null) return null;
        return typeof value.value !== 'undefined' ? value.value : value
      },
      isFieldDiscount(input) {
        return input.type === 'discount';
      },
      isDiscountConditionalVisible(input, targetValue) {
        if (!this.isFieldDiscount(input)) return false;

        let targetField = this.params ? this.params[input.discount_type_field] : null;
        if (targetField && targetField.value) {
          return targetField.value === targetValue;
        } else {
          return false;
        }
      },
      submit() {
        this.formValidated = true;
        if (this.$refs.form.checkValidity()) {
          new Promise(resolve => {
            const params = {};
            Object.keys(this.params).map((key) => {
              params[key] = this.formatValue(this.params[key], this.fields.find((item) => item.name === key))
            });
            if (params.price) {
              params.price = this.formatToCents(params.price);
            }
            if (params.trial_price) {
              params.trial_price = this.formatToCents(params.trial_price);
            }
            resolve(params)
          }).then(params => {
            this.$emit('submit', params);
          })
        } else {
          console.log('fail')
        }
      },
      clearAccount() {
        this.$emit('clear-account', this.params.id);
      },
      changeHighlightedOption(id) {
        const optionsWrapper = document.getElementById(id).parentNode.nextSibling.nextSibling;
        const optionsList = optionsWrapper.getElementsByClassName('multiselect__content')[0];
        const highlightedOption = optionsList.getElementsByClassName('multiselect__option--highlight')[0];
        const activeOption = optionsList.getElementsByClassName('multiselect__option--selected')[0];

        if (activeOption) {

          let top = activeOption.offsetTop - 120;

          optionsWrapper.scrollTo(0, top);

          highlightedOption.classList.remove("multiselect__option--highlight");
          activeOption.classList.add("multiselect__option--highlight");
        }
      },
      showInput(name) {
        if (name === 'team_id' && this.editing) {
          return false
        }
        if (!!this.params.country && !!this.params.country.name) {
          if (name === 'state') {
            return this.params.country.name.includes('United States')
          } else {
            return true
          }
        } else {
          return true
        }
      },
      addTag(name, value, remove) {
        if (!remove) {
          const tag = {
            name: value,
            value: value,
            code: value
          };
          if (!this.params[name]) {
            this.params[name] = []
          }
          this.params[name].push(tag);
        } else {
          this.params[name] = value
        }
        this.selectOptions[name] = this.params[name];
      },
      createParamsObject(name, value) {
        if (name === 'tags') {
          this.addTag(name, value, true)
        }
        this.$forceUpdate();
      },
      searchSelectOption(url, name, filter_field = null, value = null) {
        if (filter_field) {
          this.$set(this.isSelectLoading, name, true);
          const params = {filter: {}};
          if (filter_field && value) {
            params.filter[filter_field] = value
          }
          try {
            this.getData(url, {params}).then(({data}) => {
              this.selectOptions[name] = data.items.map(option => {
                if (option.title) {
                  return {
                    name: option.title,
                    value: option.title
                  }
                } else {
                  return {
                    name: option.name,
                    value: option.id
                  }
                }
              })
            }).then(() => {
              this.isSelectLoading[name] = false;
              this.$set(this.isSelectLoading, name, false);

              this.$forceUpdate();
            })
          } catch (e) {
            console.log(e);
            this.$set(this.isSelectLoading, name, false);
          } finally {
            this.$forceUpdate();
          }
        }
      },
      getSelectValues() {
        _.forEach(this.selects, select => this.assembleSelectValue(select));
      },
      assembleSelectValue(select) {
        if (!this.params[select.name] && select.defaultValue) {
          this.params[select.name] = select.defaultValue;
        }

        if (select.assembleValues) {
          if (select.groupValues) {
            this.selectOptions[select.name].forEach((item) => {
              if (item[select.groupValues]) {
                let targetItem = item[select.groupValues].find((item) => item[select.trackBy] === this.params[select.name]);

                if (targetItem) {
                  this.params[select.name] = targetItem;
                }
              }
            });
          } else {
            let targetValue = this.selectOptions[select.name].find((item) => item[select.trackBy] === this.params[select.name]);

            if (targetValue) {
              this.params[select.name] = targetValue;
            }
          }
        }
      },
      getSelectOptions() {
          _.forEach(this.selects, async (select, key) => {
            this.$set(this.selectOptions, select.name, []);

            if (select.options) {
              this.selectOptions[select.name] = select.options;
            } else if (select.storeGetter) {
              this.isSelectLoading[select.name] = true;
              this.isSelectLoading.state = true;

              try {
                let items = await this.$store.dispatch(select.storeAction);

                if (Array.isArray(items) && (this.isVisibleOnEmpty(select) || (!this.isVisibleOnEmpty(select) && items.length))) {
                  if (select.groupValues) {
                    items.forEach((group) => {
                      group[select.groupValues] = group[select.groupValues].map((item) => {
                        return {
                          id: item.id,
                          title: item.title,
                          category_id: group.id,
                          category_title: group.title
                        }
                      })
                    });
                  }

                  this.$set(this.selectOptions, select.name, items);
                  this.assembleSelectValue(select);

                  this.$forceUpdate();
                  this.isSelectLoading[select.name] = false;
                  this.isSelectLoading.state = false;
                } else {
                  this.removeField(select.name);
                }
              } catch (error) {
                console.log(error);
                this.isSelectLoading[select.name] = false;
                this.isSelectLoading.state = false;
                this.removeField(select.name);
              }
            } else if (select.options_url === 'countries') {
              this.isSelectLoading[select.name] = true;
              this.isSelectLoading.state = true;
              try {
                const {data} = await this.getCountries(select.options_url);
                this.selectOptions[select.name] = data.items.map(option => {
                  if (option.states && !!option.states.length) {
                    this.selectOptions.state = option.states.map(state => {
                      return {
                        name: state.title,
                        value: state.title
                      }
                    });
                  }
                  return {
                    name: option.title,
                    value: option.title
                  }
                });
                this.$forceUpdate();
                this.isSelectLoading[select.name] = false;
                this.isSelectLoading.state = false;
              } catch (err) {
                console.log(err);
                this.isSelectLoading[select.name] = false;
                this.isSelectLoading.state = false;
              }
            } else if (select.options_url === 'languages') {
              this.isSelectLoading[select.name] = true;
              this.isSelectLoading.state = true;
              try {
                const {data} = await this.getData(select.options_url);
                this.selectOptions[select.name] = data.items.map(option => {
                  return {
                    name: option.title,
                    value: option.id,
                  }
                });
                this.$forceUpdate();
                this.isSelectLoading[select.name] = false;
                this.isSelectLoading.state = false;
              } catch (err) {
                console.log(err);
                this.isSelectLoading[select.name] = false;
                this.isSelectLoading.state = false;
              }
            } else if (select.options_url) {
              this.isSelectLoading[select.name] = true;
              try {
                const {data} = await this.getData(select.options_url);

                if (data && data.items) {
                  this.selectOptions[select.name] = data.items.map(option => {
                    if (option.title) {
                      return {
                        name: option.title,
                        value: option.title
                      }
                    } else {
                      return {
                        name: select.dataNameKey ? option[select.dataNameKey] : option.name,
                        value: option.id
                      }
                    }
                  });
                  this.isSelectLoading[select.name] = false;
                  this.$forceUpdate();
                }
              } catch (err) {
                console.log(err);
                this.isSelectLoading[select.name] = false;
              }
            }
            this.$forceUpdate();
          })
      },
      removeField(name) {
        if (!name) return;

        let itemIndex = this.fields.findIndex((item) => item.name === name);

        this.fields.splice(itemIndex, 1);
      },
      isVisibleOnEmpty(select) {
        if (typeof select.visibleOnEmpty === "undefined") return true;

        return select.visibleOnEmpty;
      },
      priceInput (value, name) {
        this.params[name] = Math.abs(value);
      },
      getTooltipName(name) {
        return name.concat("-", this.params.id);
      }
    },
    watch: {
      emitSubmitForm() {
        this.submit()
      },
      clearFields() {
        this.formValidated = false;
        this.params = this.prefillParams;
        if (this.params.period !== undefined) {
          this.params.period = {name: "1 month", value: "1 month"}
        }
        if (this.params.currency !== undefined) {
          this.params.currency = {label: "USD", value: "USD"}
        }
      }
    }
  }
</script>
